import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Search,
  MessageSquare,
  Check,
  X,
  Loader2,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminKnowledgeApi } from '../../lib/adminApi';

interface KnowledgeItem {
  id: string;
  title: string;
  content: string;
  category: string | null;
  tags: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ChunkInfo {
  knowledge_id: string;
  chunk_count: number;
  has_embeddings: boolean;
}

interface AdminChatbotKnowledgeProps {
  noLayout?: boolean;
}

export function AdminChatbotKnowledge({ noLayout = false }: AdminChatbotKnowledgeProps = {}) {
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [chunkInfo, setChunkInfo] = useState<Map<string, ChunkInfo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<KnowledgeItem | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: ''
  });
  const [saving, setSaving] = useState(false);
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await adminKnowledgeApi.list();

      if (response.error) {
        throw new Error(response.error);
      }

      setItems(response.data.items || []);

      const chunkMap = new Map<string, ChunkInfo>();
      if (response.data.chunks) {
        response.data.chunks.forEach((chunk: ChunkInfo) => {
          chunkMap.set(chunk.knowledge_id, chunk);
        });
      }
      setChunkInfo(chunkMap);

    } catch (err) {
      console.error('Error fetching items:', err);
      setError('Fout bij ophalen van kennisbank items');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ title: '', content: '', category: '', tags: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (item: KnowledgeItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category || '',
      tags: item.tags?.join(', ') || ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Titel en content zijn verplicht');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const tagsArray = formData.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const itemData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category: formData.category.trim() || null,
        tags: tagsArray.length > 0 ? tagsArray : null
      };

      if (editingItem) {
        await adminKnowledgeApi.update(editingItem.id, itemData);
        setSuccess('Item bijgewerkt');
      } else {
        await adminKnowledgeApi.create(itemData);
        setSuccess('Item aangemaakt');
      }

      setIsModalOpen(false);
      fetchItems();

    } catch (err) {
      console.error('Error saving item:', err);
      setError('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Weet je zeker dat je dit item wilt verwijderen?')) return;

    try {
      await adminKnowledgeApi.delete(id);
      setSuccess('Item verwijderd');
      fetchItems();

    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Fout bij verwijderen');
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    try {
      await adminKnowledgeApi.toggleActive(id);
      fetchItems();

    } catch (err) {
      console.error('Error toggling active state:', err);
      setError('Fout bij wijzigen status');
    }
  };

  const generateEmbeddings = async (id: string) => {
    setGeneratingEmbeddings(id);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reindex-knowledge`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            knowledge_id: id
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Fout bij genereren embeddings');
      }

      const summary = data.summary || {};
      const totalChunks = summary.totalChunks || 0;
      setSuccess(`Embeddings gegenereerd (${totalChunks} chunks)`);
      fetchItems();

    } catch (err: any) {
      console.error('Error generating embeddings:', err);
      setError(err.message || 'Fout bij genereren embeddings');
    } finally {
      setGeneratingEmbeddings(null);
    }
  };

  const filteredItems = items.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const itemsWithoutEmbeddings = items.filter(i => !chunkInfo.get(i.id)?.has_embeddings);

  const content = (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chatbot Kennisbank</h1>
            <p className="text-gray-600 mt-1">
              Bekijk welke content geïndexeerd is voor de chatbot
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-medium text-blue-900 mb-1">Automatische indexering vanuit kennisbank</h3>
            <p className="text-sm text-blue-700">
              Alle kennisbank artikelen worden automatisch geïndexeerd wanneer ze worden aangemaakt of bijgewerkt.
              Ga naar <strong>Admin → Kennisbank</strong> om artikelen te beheren.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <X className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Zoek in kennisbank..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
              <p className="text-gray-500 mt-2">Laden...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen items gevonden</p>
              <button
                onClick={openCreateModal}
                className="mt-4 text-blue-600 hover:text-blue-700"
              >
                Voeg je eerste kennisbank item toe
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredItems.map(item => {
                const info = chunkInfo.get(item.id);
                const hasEmbeddings = info?.has_embeddings || false;

                return (
                  <div
                    key={item.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${!item.is_active ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900 truncate">
                            {item.title}
                          </h3>
                          {!item.is_active && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              Inactief
                            </span>
                          )}
                          {hasEmbeddings ? (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              {info?.chunk_count} chunks
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                              Geen embeddings
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {item.content}
                        </p>
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {item.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActive(item.id, item.is_active)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title={item.is_active ? 'Deactiveren' : 'Activeren'}
                        >
                          {item.is_active ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={() => generateEmbeddings(item.id)}
                          disabled={generatingEmbeddings === item.id}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Genereer embeddings"
                        >
                          {generatingEmbeddings === item.id ? (
                            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                          ) : (
                            <RefreshCw className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Bewerken"
                        >
                          <Pencil className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h2 className="font-medium text-gray-900 mb-4">Statistieken</h2>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-3xl font-bold text-gray-900">{items.length}</p>
              <p className="text-sm text-gray-600">Totaal items</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-3xl font-bold text-green-600">
                {items.filter(i => i.is_active).length}
              </p>
              <p className="text-sm text-gray-600">Actieve items</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-3xl font-bold text-blue-600">
                {items.filter(i => chunkInfo.get(i.id)?.has_embeddings).length}
              </p>
              <p className="text-sm text-gray-600">Met embeddings</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-3xl font-bold text-amber-600">
                {itemsWithoutEmbeddings.length}
              </p>
              <p className="text-sm text-gray-600">Zonder embeddings</p>
            </div>
          </div>
        </div>

        {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Item bewerken' : 'Nieuw item'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titel *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Bijv: Hoe werken credits?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Schrijf hier de volledige uitleg..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Gebruik synoniemen voor betere zoekresultaten
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categorie
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Bijv: FAQ, Handleiding, Pricing"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (komma gescheiden)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="credits, kosten, betalen, kopen"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Opslaan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
  );

  if (noLayout) {
    return content;
  }

  return <AdminLayout>{content}</AdminLayout>;
}
